import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import Badge from '../ui/Badge';
import { faqData } from '../../data/mockData';

export default function Guide() {
  const [activeStep, setActiveStep] = useState(0);
  const [simState, setSimState] = useState('success');

  const steps = [
    { title: 'Connect Client', desc: 'Add a new lead to the Pipeline from the Leads board.', icon: 'UserPlus' },
    { title: 'Scope Budget', desc: 'Define project value and set profitability targets.', icon: 'Target' },
    { title: 'Track Burn', desc: 'Monitor real-time spending against project budget.', icon: 'Activity' },
    { title: 'Milestones', desc: 'Map deliverables to the Studio board for team visibility.', icon: 'CheckSquare' },
    { title: 'Generate Invoice', desc: 'Auto-fill invoice data from project milestones.', icon: 'FileText' },
    { title: 'Analyze Profit', desc: 'Review the Profit Snapshot for margin health.', icon: 'TrendingUp' }
  ];

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700 max-w-5xl mx-auto pb-20">
      {/* Overview Section */}
      <section className="text-center space-y-4 pt-4">
        <h1 className="text-4xl font-light text-cream tracking-tight">The Agency Operating System.</h1>
        <p className="text-lg text-cream-muted max-w-2xl mx-auto leading-relaxed">
          StudioLedger centralizes client relationships, project burn rates, and cash flow for small creative agencies. 
          Stop guessing your margins and start scaling with data-driven confidence.
        </p>
      </section>

      {/* Demo Workflow Section */}
      <section className="glass-panel rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-[#333] bg-charcoal-light/30">
          <h2 className="text-xl font-medium text-cream flex items-center gap-2">
            <SafeIcon name="Play" className="w-5 h-5 text-invoice-green" />
            How It Works: Try the Flow
          </h2>
        </div>
        <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-4">
            {steps.map((step, idx) => (
              <button 
                key={idx}
                onClick={() => setActiveStep(idx)}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-300 flex items-center gap-4 ${
                  activeStep === idx 
                    ? 'bg-invoice-green/10 border-invoice-green/30 text-cream' 
                    : 'bg-transparent border-[#333] text-cream-muted hover:border-[#444]'
                }`}
              >
                <div className={`p-2 rounded-lg ${activeStep === idx ? 'bg-invoice-green text-[#111]' : 'bg-charcoal-light'}`}>
                  <SafeIcon name={step.icon} className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">{idx + 1}. {step.title}</h4>
                  {activeStep === idx && <p className="text-xs mt-1 text-cream-muted">{step.desc}</p>}
                </div>
              </button>
            ))}
          </div>
          
          <div className="glass-panel rounded-xl p-6 bg-charcoal-base flex flex-col justify-center items-center text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-invoice-green/10 flex items-center justify-center animate-pulse">
              <SafeIcon name={steps[activeStep].icon} className="w-10 h-10 text-invoice-green" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-medium text-cream">Step {activeStep + 1}: {steps[activeStep].title}</h3>
              <p className="text-sm text-cream-muted italic">"StudioLedger automates this step to ensure no billable hour is lost."</p>
            </div>
            <div className="pt-4 w-full">
               <div className="h-1 w-full bg-[#222] rounded-full overflow-hidden">
                 <div 
                   className="h-full bg-invoice-green transition-all duration-500" 
                   style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
                 />
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simulated States Section */}
      <section className="space-y-6">
        <div className="flex justify-between items-end">
          <div>
            <h2 className="text-xl font-medium text-cream">System State Previews</h2>
            <p className="text-sm text-cream-muted">Experience how StudioLedger handles different data scenarios.</p>
          </div>
          <div className="flex gap-2 bg-charcoal-panel p-1 rounded-lg border border-[#333]">
            {['success', 'warning', 'empty'].map(s => (
              <button 
                key={s}
                onClick={() => setSimState(s)}
                className={`px-3 py-1 rounded-md text-xs capitalize transition-colors ${
                  simState === s ? 'bg-invoice-green text-[#111]' : 'text-cream-muted hover:text-cream'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="glass-panel rounded-2xl p-12 flex flex-col items-center justify-center min-h-[300px] text-center">
          {simState === 'success' && (
            <div className="space-y-4 animate-in zoom-in-95 duration-300">
              <div className="w-16 h-16 rounded-full bg-invoice-green/20 flex items-center justify-center mx-auto">
                <SafeIcon name="Check" className="w-8 h-8 text-invoice-green" />
              </div>
              <h3 className="text-lg font-medium text-cream">Payment Confirmed</h3>
              <p className="text-sm text-cream-muted">Invoice INV-2042 was successfully settled via ACH.</p>
              <Badge variant="success">Matched with Bank Record</Badge>
            </div>
          )}
          {simState === 'warning' && (
            <div className="space-y-4 animate-in zoom-in-95 duration-300">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                <SafeIcon name="AlertCircle" className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-cream">Budget Threshold Met</h3>
              <p className="text-sm text-cream-muted">Project "Globex UI" is currently at 95% budget utilization.</p>
              <Badge variant="danger">Attention Required</Badge>
            </div>
          )}
          {simState === 'empty' && (
            <div className="space-y-4 animate-in zoom-in-95 duration-300">
              <div className="w-16 h-16 rounded-full bg-charcoal-light flex items-center justify-center mx-auto text-cream-muted">
                <SafeIcon name="Inbox" className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-medium text-cream">No Pending Deliverables</h3>
              <p className="text-sm text-cream-muted">Your task board is clear. Time to source new leads!</p>
              <Badge>Ready for Intake</Badge>
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="space-y-6">
        <h2 className="text-xl font-medium text-cream text-center">Frequently Asked Questions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {faqData.map((faq, i) => (
            <div key={i} className="glass-panel p-6 rounded-xl space-y-3">
              <h4 className="text-cream font-medium flex items-start gap-2">
                <span className="text-invoice-green mt-1">Q:</span>
                {faq.q}
              </h4>
              <p className="text-sm text-cream-muted leading-relaxed">
                <span className="text-cream font-bold mr-1">A:</span>
                {faq.a}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}